import { useEffect, useState } from 'react';

// Each set of games lives on its own subdomain, e.g. auw26-vm929.maccas-deals.com →
// "auw26" (Adelaide United women's 2026). The prefix picks the logo and T&Cs from
// public/logos/<prefix>.png and public/terms/<prefix>.txt, so adding a series is just
// adding those two files — no code change or rebuild.
//
// T&Cs are shown as written; separate paragraphs with a blank line.
// Public files keep their names across builds, so give /logos/* and /terms/* a short cache
// lifetime on the CDN or a replaced file may be served stale.
const SERIES_PATTERN = /^[a-z]+\d{2}$/;

function readSeries() {
  // localhost has no subdomain: use ?series=auw26, or REACT_APP_DEV_SERIES in .env.
  if (process.env.NODE_ENV === 'development') {
    const fromQuery = new URLSearchParams(window.location.search).get('series');
    const devSeries = fromQuery || process.env.REACT_APP_DEV_SERIES;
    if (devSeries) return SERIES_PATTERN.test(devSeries) ? devSeries : null;
  }

  const prefix = window.location.hostname.split('.')[0].split('-')[0];
  return SERIES_PATTERN.test(prefix) ? prefix : null;
}

export const SERIES = readSeries();

if (!SERIES) console.error(`No series prefix found in hostname "${window.location.hostname}"`);

export const seriesLogoUrl = SERIES ? `${process.env.PUBLIC_URL}/logos/${SERIES}.png` : null;

// Fetched once and shared by every TermsModal.
let termsPromise;

function loadTerms() {
  if (!termsPromise) {
    termsPromise = SERIES
      ? fetch(`${process.env.PUBLIC_URL}/terms/${SERIES}.txt`, { cache: 'no-cache' })
          .then(async (response) => {
            const text = await response.text();
            // The dev server (and SPA-configured CDNs) answer a missing file with index.html
            // and a 200, so check we actually got text rather than the app's HTML.
            const isText = (response.headers.get('content-type') || '').startsWith('text/plain');
            if (!response.ok || !isText || text.trimStart().startsWith('<')) {
              throw new Error(`T&Cs not found for series "${SERIES}"`);
            }
            return text;
          })
          .catch((error) => {
            console.error(error);
            return null;
          })
      : Promise.resolve(null);
  }
  return termsPromise;
}

// Starts the request as soon as the app loads so the T&Cs are ready before anyone opens them.
loadTerms();

// undefined while loading, null if unavailable, otherwise the raw template text.
export function useSeriesTerms() {
  const [terms, setTerms] = useState(undefined);
  useEffect(() => {
    let active = true;
    loadTerms().then((text) => active && setTerms(text));
    return () => {
      active = false;
    };
  }, []);
  return terms;
}
